import React, { useState } from "react";
import { X } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const NewFreeTrial = ({ show, handleClose, pageKey }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    website: "",
    contact: "",
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.contact) {
      toast.warning("Please fill all required fields");
      return;
    }

    const payload = {
      fullname: formData.fullName,
      email: formData.email,
      website: formData.website,
      contact: formData.contact,
    };

    try {
      setLoading(true);

      const response = await fetch(
        "https://auth.seobix.com/api/Auth/InsertLeads",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (data?.status === "0") {
        toast.success(
          data?.result?.[0]?.message || "Form submitted successfully"
        );

        if (pageKey) {
          sessionStorage.setItem(`freeTrialClosed_${pageKey}`, "true");
        }

        setFormData({
          fullName: "",
          email: "",
          website: "",
          contact: "",
        });

        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        toast.error("Submission failed. Please try again.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error. Please try later.");
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    if (pageKey) {
      sessionStorage.setItem(`freeTrialClosed_${pageKey}`, "true");
    }
    handleClose();
  };

  if (!show) return null;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
      />

      <div style={overlayStyle}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <style>{`
            @keyframes modalFadeIn {
              from {
                opacity: 0;
                transform: translateY(-20px) scale(0.98);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>

          <div style={{ padding: "1.25rem", position: "relative" }}>
            <button onClick={handleModalClose} style={closeButtonStyle}>
              <X size={16} />
            </button>

            <h3
              style={{
                fontWeight: "700",
                marginBottom: "0.5rem",
                textAlign: "center",
                color: "#01C37D",
              }}
            >
              Start Your Free Trial
            </h3>

            <p
              style={{
                color: "#6c757d",
                textAlign: "center",
                marginBottom: "1rem",
                fontSize: "14px",
              }}
            >
              Please fill out the form below and we will ensure a personalised
              demo catering to your requirements.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "0.75rem" }}>
                <label style={labelStyle}>
                  Full Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter Full Name"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "0.75rem" }}>
                <label style={labelStyle}>
                  Work Email <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter Work Email"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "0.75rem" }}>
                <label style={labelStyle}>Website (Optional)</label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="www.website.com"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={labelStyle}>
                  Contact Number <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  placeholder="Contact Number"
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={submitButtonStyle}
                >
                  {loading ? "Submitting..." : "Start Free Trial"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.55)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
};

const modalStyle = {
  maxWidth: "500px",
  width: "100%",
  background: "#fff",
  borderRadius: "18px",
  position: "relative",
  animation: "modalFadeIn 0.3s ease-out",
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
};

const closeButtonStyle = {
  position: "absolute",
  top: "12px",
  right: "12px",
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: "none",
  background: "#f5f5f5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const labelStyle = {
  color: "#02744a",
  fontWeight: 500,
  marginBottom: "6px",
  display: "inline-block",
};

const inputStyle = {
  width: "100%",
  padding: "0.75rem",
  border: "1px solid #ced4da",
  borderRadius: "10px",
  fontSize: "1rem",
  boxSizing: "border-box",
  outline: "none",
};

const submitButtonStyle = {
  width: "100%",
  padding: "0.85rem",
  backgroundColor: "#03a167",
  border: "none",
  borderRadius: "999px",
  color: "white",
  fontWeight: "600",
  cursor: "pointer",
};

export default NewFreeTrial;